import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { articleSchema } from '../components/JsonLd';

const Example = ({ children }) => (
  <div className="sf-example">
    <div className="sf-example-label">Real-world example</div>
    <p>{children}</p>
  </div>
);

const RiskManagement = () => {
  return (
    <>
      <SEO
        title="Risk Management for Investors - How to Protect Your Portfolio | StckFuture"
        description="Learn investment risk management including diversification, position sizing, stop-loss strategies, hedging, and how to protect your portfolio from losses."
        canonical="https://www.stckfuture.com/risk-management"
      />
      <JsonLd schema={articleSchema({
        title: 'Risk Management for Investors - How to Protect Your Portfolio | StckFuture',
        description: 'Learn investment risk management including diversification, position sizing, stop-loss strategies, hedging, and how to protect your portfolio.',
        url: 'https://www.stckfuture.com/risk-management',
        dateModified: '2026-06-13',
      })} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 4</span>
                <h1>Risk Management</h1>
                <p className="lead">Successful investors don't avoid risk — they identify, measure, and manage it. Learn how to protect your capital while still allowing it to grow.</p>
              </div>

              {/* Understanding Risk */}
              <div className="sf-card">
                <h3>Understanding risk</h3>
                <p>All investments carry risk. The goal isn't to eliminate risk (that's impossible) — it's to take calculated risks where the potential reward justifies the downside.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Market risk (systematic)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Affects the entire market — recessions, interest rate changes, pandemics. Cannot be eliminated through diversification. Every investor bears this.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Company risk (unsystematic)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Specific to an individual company — bad earnings, management failure, scandal. Can be dramatically reduced through diversification.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Inflation risk</strong>
                    <p className="sf-text-small sf-text-muted mt-1">The risk that returns don't keep pace with inflation, eroding purchasing power over time. Even "safe" savings accounts carry this risk in a high-inflation environment.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Liquidity risk</strong>
                    <p className="sf-text-small sf-text-muted mt-1">The risk of not being able to sell an investment quickly at fair market value. Real estate and some bonds are more illiquid than stocks.</p>
                  </div>
                </div>
                <Example>In March 2020, when COVID-19 was declared a pandemic, the S&P 500 fell ~34% in just over a month — pure market risk that hit almost everything. But Zoom and Amazon actually rose significantly during the same period, demonstrating that company-specific risk and market risk are different forces.</Example>
              </div>

              {/* Diversification */}
              <div className="sf-card">
                <h3>Diversification</h3>
                <p>Spreading investments across different assets reduces the impact of any single investment performing badly. The key insight: assets that don't move together reduce your overall volatility.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Asset class diversification</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Stocks, bonds, real estate, commodities — each responds differently to economic conditions.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Sector diversification</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Spread across tech, healthcare, finance, energy, consumer goods — sector downturns won't wipe out your portfolio.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Geographic diversification</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Domestic and international markets reduce exposure to any single country's economic or political risk.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Time diversification</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Dollar-cost averaging spreads your entry point across time, reducing the impact of investing at a peak.</p>
                  </div>
                </div>
                <Example>During the 2008 financial crisis, a portfolio of 100% U.S. stocks lost ~37%. A diversified portfolio of 40% U.S. stocks, 20% international stocks, 30% bonds, and 10% commodities lost only ~20% — and recovered significantly faster in the years that followed.</Example>
              </div>

              {/* Position Sizing */}
              <div className="sf-card">
                <h3>Position sizing</h3>
                <p>No matter how confident you are in a trade or investment, limiting the percentage of your portfolio in any single position is what protects you from catastrophic loss.</p>
                <ul className="mb-3">
                  <li className="mb-2"><strong>Percentage of portfolio</strong> — a common rule: no single stock position exceeds 5% of your total portfolio. Limits damage from any one company failing.</li>
                  <li className="mb-2"><strong>Risk-based sizing</strong> — higher-risk investments get smaller allocations than lower-risk ones, proportional to potential downside.</li>
                  <li className="mb-2"><strong>Kelly Criterion</strong> — a mathematical formula used by professional traders to determine the optimal position size based on edge and odds.</li>
                </ul>
              </div>

              {/* Stop-Loss Strategies */}
              <div className="sf-card">
                <h3>Stop-loss strategies</h3>
                <p>A stop-loss is a predetermined price at which you'll exit a position to limit your downside. Setting them before you enter a trade — not after you're already losing — is the key to using them effectively.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Fixed percentage stop</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Exit when a position falls by a set percentage, e.g. 7–10% below your purchase price. Simple and consistent.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Technical stop</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Based on support levels or moving averages — placed just below a major support level where your thesis would be invalidated.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Trailing stop</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Moves up as the position gains value — locks in profits while still allowing the position to run if the trend continues.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Volatility-based stop</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Wider stops for volatile assets, tighter for stable ones. Prevents getting stopped out by normal price fluctuation.</p>
                  </div>
                </div>
              </div>

              {/* Correlation */}
              <div className="sf-card">
                <h3>Correlation & portfolio risk</h3>
                <p>Correlation measures how two investments move relative to each other. True diversification requires assets that don't all move together — especially in a crisis.</p>
                <div className="row g-3">
                  <div className="col-md-4">
                    <strong>Positive (+1.0)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Assets move together. Example: two large-cap U.S. tech stocks — owning both provides less diversification than it appears.</p>
                  </div>
                  <div className="col-md-4">
                    <strong>No correlation (0)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Assets move independently. Example: gold often has low or zero correlation to stocks — useful for diversification.</p>
                  </div>
                  <div className="col-md-4">
                    <strong>Negative (−1.0)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Assets move opposite. Example: Treasury bonds often move opposite to stocks during crises — the best diversifier in a crash.</p>
                  </div>
                </div>
              </div>

              {/* Hedging */}
              <div className="sf-card">
                <h3>Hedging strategies</h3>
                <p>Hedging means taking an offsetting position to reduce the impact of adverse price movements. It's like buying insurance — you pay a small cost to protect against a larger potential loss.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Put options</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Give you the right to sell shares at a set price. Buying puts on a stock you own limits your downside if it falls sharply.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Inverse ETFs</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Move opposite to their underlying index. Can offset losses in long positions during market-wide declines.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Portfolio hedging</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Buying S&P 500 put options to protect a diversified stock portfolio against broad market risk.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Currency hedging</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Protects international investments against currency fluctuations — especially relevant for large cross-border positions.</p>
                  </div>
                </div>
              </div>

              {/* Risk-Adjusted Returns */}
              <div className="sf-card">
                <h3>Risk-adjusted returns</h3>
                <p>A high return means little if it came with extreme risk. These metrics let you compare investments fairly by measuring how much return you got per unit of risk taken.</p>
                <ul>
                  <li className="mb-2"><strong>Sharpe ratio</strong> — (Return − Risk-free rate) ÷ Standard deviation. Higher is better. Measures excess return per unit of total volatility.</li>
                  <li className="mb-2"><strong>Sortino ratio</strong> — Like Sharpe, but only counts downside volatility. More relevant for investors focused on avoiding losses.</li>
                  <li className="mb-2"><strong>Maximum drawdown</strong> — the largest peak-to-trough decline. Tells you the worst-case historical loss you would have experienced.</li>
                  <li className="mb-2"><strong>Calmar ratio</strong> — Annual return ÷ Maximum drawdown. Useful for comparing strategies with different risk profiles.</li>
                </ul>
              </div>

              {/* Risk by Life Stage */}
              <div className="sf-card">
                <h3>Risk management by life stage</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <strong>20s–30s</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li>Long time horizon = higher risk capacity</li>
                      <li>Focus on growth with higher equity allocation</li>
                      <li>Use dollar-cost averaging consistently</li>
                      <li>Build emergency fund before investing</li>
                    </ul>
                  </div>
                  <div className="col-md-4">
                    <strong>40s–50s</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li>Balance growth with capital preservation</li>
                      <li>Diversify across more asset classes</li>
                      <li>Reduce single-company concentration</li>
                      <li>Begin implementing hedging strategies</li>
                    </ul>
                  </div>
                  <div className="col-md-4">
                    <strong>60s+</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li>Shift focus to income and preservation</li>
                      <li>Reduce volatility exposure</li>
                      <li>Maintain liquidity for near-term expenses</li>
                      <li>Plan for longevity risk (outliving savings)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Key Takeaways */}
              <div className="sf-takeaways">
                <h3>Key takeaways</h3>
                <ul>
                  <li>Risk management isn't about avoiding risk — it's about taking calculated risks with appropriate sizing</li>
                  <li>Diversification across asset classes, sectors, and geographies is the most reliable risk reducer</li>
                  <li>Position sizing limits how much any single investment can hurt your overall portfolio</li>
                  <li>Set stop-losses before entering trades — not after you're already losing</li>
                  <li>Correlation matters: true diversification requires assets that don't all move together</li>
                  <li>Hedging strategies provide downside protection, like buying insurance for your portfolio</li>
                  <li>Risk tolerance and strategy should evolve as you age and your financial situation changes</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/market-analysis" className="btn-sf-primary">
                  Next: Market Analysis →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RiskManagement;